import React, { useState, useEffect } from "react";
import axios from "axios";
import { baseApiURL } from "../../baseUrl";
import toast from "react-hot-toast";
import { motion, AnimatePresence } from "framer-motion";
import {
  FiUsers,
  FiCalendar,
  FiClock,
  FiMapPin,
  FiImage,
  FiSearch,
  FiX,
  FiCheckCircle,
  FiAlertCircle,
} from "react-icons/fi";

const DailyFacultyAttendance = ({ branch }) => {
  // If branch prop is passed, it is the HOD view. Otherwise, it is the Admin view.
  const isHOD = !!branch;
  const [selectedBranch, setSelectedBranch] = useState(branch || "All");
  const [branches, setBranches] = useState([]);
  const [date, setDate] = useState(() => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, "0");
    const day = String(today.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  });
  const [statusFilter, setStatusFilter] = useState("All");
  const [searchQuery, setSearchQuery] = useState("");
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  
  // Modal states
  const [showPhotoModal, setShowPhotoModal] = useState(false);
  const [modalPhotoUrl, setModalPhotoUrl] = useState("");
  const [modalLogInfo, setModalLogInfo] = useState(null);

  // Fetch branches for admin
  useEffect(() => {
    if (!isHOD) {
      fetchBranches();
    }
  }, [isHOD]);

  // Fetch logs whenever date or selectedBranch changes
  useEffect(() => {
    fetchLogs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [date, selectedBranch]);

  const fetchBranches = async () => {
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get(`${baseApiURL()}/branch/getBranch`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.data.success) {
        setBranches(res.data.branches);
      }
    } catch (error) {
      console.error("Error fetching branches:", error);
    }
  };

  const fetchLogs = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const branchParam = selectedBranch === "All" ? "" : selectedBranch;
      const res = await axios.get(
        `${baseApiURL()}/biometric-attendance/logs?date=${date}&branch=${branchParam}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      if (res.data.success) {
        setLogs(res.data.logs);
      }
    } catch (error) {
      toast.error("Failed to load daily attendance logs");
    } finally {
      setLoading(false);
    }
  };

  // Filter logs based on search and status
  const filteredLogs = logs.filter((log) => {
    const matchesSearch =
      log.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      log.facultyId.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus =
      statusFilter === "All" || log.status.toLowerCase() === statusFilter.toLowerCase();
    return matchesSearch && matchesStatus;
  });

  const getStats = () => {
    const activeCheckins = logs.filter((l) => l.status === "Checked-In").length;
    const completed = logs.filter((l) => l.status === "Completed").length;
    const total = logs.length;
    return { activeCheckins, completed, total };
  };

  const stats = getStats();

  const handleOpenPhotoModal = (photoUrl, log, type) => {
    setModalPhotoUrl(photoUrl);
    setModalLogInfo({
      name: log.name,
      facultyId: log.facultyId,
      type: type === "checkin" ? "Check-In Photo" : "Check-Out Photo",
      time: type === "checkin" ? log.checkInTime : log.checkOutTime,
      distance: type === "checkin" ? log.checkInDistance : log.checkOutDistance,
      location: type === "checkin" ? log.checkInLocation : log.checkOutLocation,
    });
    setShowPhotoModal(true);
  };

  return (
    <div className="w-full space-y-6">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <div>
            <h1 className="text-xl md:text-2xl font-bold text-slate-900 dark:text-white tracking-tight flex items-center">
              <FiUsers className="mr-3 text-indigo-600 dark:text-indigo-400" />
              {isHOD ? `${branch} Daily Attendance` : "Faculty Daily Attendance"}
            </h1>
            <p className="text-slate-500 dark:text-slate-400 text-xs md:text-sm mt-1">
              {isHOD
                ? `Track today's check-in/check-out biometric logs for the ${branch} branch`
                : "Monitor and audit daily facial biometric attendance with geofence metrics across all branches"}
            </p>
          </div>

          {/* Quick Filters */}
          <div className="flex flex-wrap gap-3 bg-white dark:bg-slate-900 p-2 rounded-2xl shadow-xs border border-slate-200 dark:border-slate-800 w-full md:w-auto">
            {/* Date Selection */}
            <div className="flex items-center space-x-2 px-3 border-r border-slate-100 dark:border-slate-800">
              <FiCalendar className="text-slate-400 dark:text-slate-500" />
              <input
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                className="text-xs font-bold outline-none bg-transparent cursor-pointer text-slate-700 dark:text-slate-300"
              />
            </div>

            {/* Branch Selection (Admin only) */}
            {!isHOD && (
              <select
                value={selectedBranch}
                onChange={(e) => setSelectedBranch(e.target.value)}
                className="bg-transparent px-3 py-1 text-xs font-bold outline-none text-slate-700 dark:text-slate-300 cursor-pointer"
              >
                <option value="All">All Departments</option>
                {branches.map((b) => (
                  <option key={b._id} value={b.name}>
                    {b.name}
                  </option>
                ))}
              </select>
            )}
          </div>
        </div>

        {/* Stats Row */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <motion.div
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white dark:bg-slate-900 rounded-2xl p-5 shadow-xs border border-slate-200 dark:border-slate-800 flex items-center space-x-4"
          >
            <div className="bg-indigo-50 dark:bg-indigo-950/60 p-3.5 rounded-xl text-indigo-600 dark:text-indigo-400">
              <FiUsers className="text-2xl" />
            </div>
            <div>
              <p className="text-xs font-bold text-slate-400 uppercase tracking-wider">Total Active Logs</p>
              <h3 className="text-2xl font-black text-slate-900 dark:text-white">{stats.total}</h3>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-white dark:bg-slate-900 rounded-2xl p-5 shadow-xs border border-slate-200 dark:border-slate-800 flex items-center space-x-4"
          >
            <div className="bg-amber-50 dark:bg-amber-950/60 p-3.5 rounded-xl text-amber-600 dark:text-amber-400">
              <FiClock className="text-2xl" />
            </div>
            <div>
              <p className="text-xs font-bold text-slate-400 uppercase tracking-wider">Active Check-ins</p>
              <h3 className="text-2xl font-black text-slate-900 dark:text-white">{stats.activeCheckins}</h3>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-white dark:bg-slate-900 rounded-2xl p-5 shadow-xs border border-slate-200 dark:border-slate-800 flex items-center space-x-4"
          >
            <div className="bg-emerald-50 dark:bg-emerald-950/60 p-3.5 rounded-xl text-emerald-600 dark:text-emerald-400">
              <FiCheckCircle className="text-2xl" />
            </div>
            <div>
              <p className="text-xs font-bold text-slate-400 uppercase tracking-wider">Completed Logs</p>
              <h3 className="text-2xl font-black text-slate-900 dark:text-white">{stats.completed}</h3>
            </div>
          </motion.div>
        </div>

        {/* Filters and List */}
        <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-xs border border-slate-200 dark:border-slate-800 overflow-hidden">
          {/* Controls Bar */}
          <div className="p-5 border-b border-slate-100 dark:border-slate-800 flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div className="relative flex-1 max-w-md">
              <span className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none text-slate-400">
                <FiSearch />
              </span>
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search by Faculty Name or ID..."
                className="w-full pl-10 pr-4 py-2.5 bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-xl outline-none focus:ring-2 focus:ring-indigo-500 font-medium text-slate-900 dark:text-white text-xs"
              />
            </div>

            <div className="flex items-center space-x-1.5 bg-slate-100 dark:bg-slate-950 p-1 rounded-xl border border-slate-200 dark:border-slate-800">
              {["All", "Checked-In", "Completed"].map((status) => (
                <button
                  key={status}
                  onClick={() => setStatusFilter(status)}
                  className={`px-3.5 py-1.5 rounded-lg text-xs font-bold transition cursor-pointer ${
                    statusFilter === status
                      ? "bg-white dark:bg-slate-800 text-indigo-600 dark:text-indigo-400 shadow-xs"
                      : "text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white"
                  }`}
                >
                  {status}
                </button>
              ))}
            </div>
          </div>

          {/* List Table */}
          <div className="overflow-x-auto">
            {loading ? (
              <div className="flex flex-col items-center justify-center py-20 space-y-4">
                <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-indigo-600"></div>
                <p className="text-slate-500 dark:text-slate-400 font-medium text-xs">Fetching attendance records...</p>
              </div>
            ) : filteredLogs.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-20 text-slate-400 px-6">
                <FiAlertCircle size={40} className="mb-3 opacity-40 text-indigo-500" />
                <h3 className="text-base font-bold text-slate-900 dark:text-white mb-1">No Daily Records Found</h3>
                <p className="text-xs text-slate-500 dark:text-slate-400 max-w-sm text-center">
                  No attendance logs found for this date/branch combination. Ensure faculty have checked in.
                </p>
              </div>
            ) : (
              <table className="w-full text-left divide-y divide-slate-200 dark:divide-slate-800 border-collapse text-xs">
                <thead className="bg-slate-50 dark:bg-slate-950 text-slate-600 dark:text-slate-400 font-bold uppercase tracking-wider">
                  <tr>
                    <th className="px-6 py-3.5">Faculty Member</th>
                    <th className="px-6 py-3.5">Department</th>
                    <th className="px-6 py-3.5">Check-In details</th>
                    <th className="px-6 py-3.5">Check-Out details</th>
                    <th className="px-6 py-3.5 text-center">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 dark:divide-slate-800 text-slate-800 dark:text-slate-200">
                  {filteredLogs.map((log) => (
                    <tr key={log._id} className="hover:bg-slate-50/50 dark:hover:bg-slate-800/30 transition-colors">
                      {/* Name & ID */}
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center space-x-3">
                          <div className="w-10 h-10 bg-indigo-50 dark:bg-indigo-950/60 text-indigo-600 dark:text-indigo-400 rounded-xl flex items-center justify-center font-bold">
                            {log.name[0]}
                          </div>
                          <div>
                            <div className="font-bold text-slate-900 dark:text-white text-xs">{log.name}</div>
                            <div className="text-[11px] text-slate-400 font-mono font-semibold">{log.facultyId}</div>
                          </div>
                        </div>
                      </td>

                      {/* Branch */}
                      <td className="px-6 py-4 whitespace-nowrap text-xs font-semibold text-slate-600 dark:text-slate-400">
                        {log.department}
                      </td>

                      {/* Check-In Details */}
                      <td className="px-6 py-4 whitespace-nowrap">
                        {log.checkInTime ? (
                          <div className="flex items-center space-x-3">
                            <button
                              onClick={() => handleOpenPhotoModal(log.checkInPhotoUrl, log, "checkin")}
                              className="group w-11 h-11 rounded-xl overflow-hidden border border-slate-200 dark:border-slate-800 relative flex items-center justify-center bg-slate-50 dark:bg-slate-950 cursor-pointer shadow-xs hover:border-indigo-500 transition-all"
                            >
                              {log.checkInPhotoUrl && log.checkInPhotoUrl !== "uploading" ? (
                                <img
                                  src={log.checkInPhotoUrl}
                                  alt="Check-in Snapshot"
                                  className="w-full h-full object-cover group-hover:scale-110 transition-transform"
                                />
                              ) : log.checkInPhotoUrl === "uploading" ? (
                                <div className="absolute inset-0 flex items-center justify-center bg-slate-100 dark:bg-slate-800">
                                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-indigo-600 border-t-transparent"></div>
                                </div>
                              ) : (
                                <FiImage className="text-slate-400" />
                              )}
                            </button>
                            <div>
                              <div className="text-xs font-bold text-slate-800 dark:text-slate-200 flex items-center">
                                <FiClock className="mr-1 text-slate-400" size={11} /> {log.checkInTime}
                              </div>
                              <div className="text-[10px] text-slate-400 font-bold flex items-center mt-0.5">
                                <FiMapPin className="mr-0.5 text-indigo-500" size={10} />
                                {log.checkInDistance !== null ? `${log.checkInDistance}m inside` : "Inside Geofence"}
                              </div>
                            </div>
                          </div>
                        ) : (
                          <span className="text-xs text-slate-400 italic">Not Checked-In</span>
                        )}
                      </td>

                      {/* Check-Out Details */}
                      <td className="px-6 py-4 whitespace-nowrap">
                        {log.checkOutTime ? (
                          <div className="flex items-center space-x-3">
                            <button
                              onClick={() => handleOpenPhotoModal(log.checkOutPhotoUrl, log, "checkout")}
                              className="group w-11 h-11 rounded-xl overflow-hidden border border-slate-200 dark:border-slate-800 relative flex items-center justify-center bg-slate-50 dark:bg-slate-950 cursor-pointer shadow-xs hover:border-indigo-500 transition-all"
                            >
                              {log.checkOutPhotoUrl && log.checkOutPhotoUrl !== "uploading" ? (
                                <img
                                  src={log.checkOutPhotoUrl}
                                  alt="Check-out Snapshot"
                                  className="w-full h-full object-cover group-hover:scale-110 transition-transform"
                                />
                              ) : log.checkOutPhotoUrl === "uploading" ? (
                                <div className="absolute inset-0 flex items-center justify-center bg-slate-100 dark:bg-slate-800">
                                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-indigo-600 border-t-transparent"></div>
                                </div>
                              ) : (
                                <FiImage className="text-slate-400" />
                              )}
                            </button>
                            <div>
                              <div className="text-xs font-bold text-slate-800 dark:text-slate-200 flex items-center">
                                <FiClock className="mr-1 text-slate-400" size={11} /> {log.checkOutTime}
                              </div>
                              <div className="text-[10px] text-slate-400 font-bold flex items-center mt-0.5">
                                <FiMapPin className="mr-0.5 text-indigo-500" size={10} />
                                {log.checkOutDistance !== null ? `${log.checkOutDistance}m inside` : "Inside Geofence"}
                              </div>
                            </div>
                          </div>
                        ) : log.checkInTime ? (
                          <div className="flex items-center space-x-2 text-amber-500 text-xs font-bold animate-pulse">
                            <FiClock size={11} /> <span>Awaiting Check-Out</span>
                          </div>
                        ) : (
                          <span className="text-xs text-slate-400 italic">Awaiting Check-in</span>
                        )}
                      </td>

                      {/* Status */}
                      <td className="px-6 py-4 whitespace-nowrap text-center">
                        <span
                          className={`px-3 py-1 rounded-full text-[11px] font-bold tracking-wide ${
                            log.status === "Completed"
                              ? "bg-emerald-50 dark:bg-emerald-950/60 text-emerald-600 dark:text-emerald-400 border border-emerald-100 dark:border-emerald-800"
                              : "bg-indigo-50 dark:bg-indigo-950/60 text-indigo-600 dark:text-indigo-400 border border-indigo-150 dark:border-indigo-800 animate-pulse"
                          }`}
                        >
                          {log.status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>

      {/* Audit Photo Modal */}
      <AnimatePresence>
        {showPhotoModal && (
          <div className="fixed inset-0 bg-slate-950/75 backdrop-blur-xs flex items-center justify-center z-[100] p-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-white dark:bg-slate-900 rounded-2xl overflow-hidden max-w-md w-full shadow-2xl border border-slate-200 dark:border-slate-800"
            >
              {/* Photo Frame */}
              <div className="relative aspect-square w-full bg-slate-950 flex items-center justify-center">
                {modalPhotoUrl && modalPhotoUrl !== "uploading" ? (
                  <img src={modalPhotoUrl} alt="Audit Snapshot" className="w-full h-full object-cover" />
                ) : modalPhotoUrl === "uploading" ? (
                  <div className="text-white flex flex-col items-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-4 border-indigo-500 border-t-transparent mb-3"></div>
                    <span className="text-xs text-slate-400">Uploading photo...</span>
                  </div>
                ) : (
                  <div className="text-white flex flex-col items-center">
                    <FiImage size={48} className="opacity-20 mb-3" />
                    <span>No image captured</span>
                  </div>
                )}
                <button
                  onClick={() => setShowPhotoModal(false)}
                  className="absolute top-4 right-4 bg-black/50 text-white rounded-full p-2 hover:bg-black/75 transition cursor-pointer border border-white/10"
                >
                  <FiX size={16} />
                </button>
              </div>

              {/* Log Info Panel */}
              {modalLogInfo && (
                <div className="p-6 space-y-4">
                  <div>
                    <span className="bg-indigo-50 dark:bg-indigo-950/60 text-indigo-600 dark:text-indigo-400 px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider">
                      {modalLogInfo.type}
                    </span>
                    <h3 className="text-base font-bold text-slate-900 dark:text-white mt-2">{modalLogInfo.name}</h3>
                    <p className="text-slate-400 text-xs font-mono font-semibold">{modalLogInfo.facultyId}</p>
                  </div>

                  <div className="grid grid-cols-2 gap-4 border-t border-slate-100 dark:border-slate-800 pt-4 text-xs font-semibold">
                    <div className="space-y-1">
                      <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Captured Time</p>
                      <p className="text-slate-700 dark:text-slate-300 flex items-center text-xs">
                        <FiClock className="mr-1 text-slate-400" /> {modalLogInfo.time || "N/A"}
                      </p>
                    </div>
                    <div className="space-y-1">
                      <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Geofence Distance</p>
                      <p className="text-slate-700 dark:text-slate-300 flex items-center text-xs">
                        <FiMapPin className="mr-1 text-indigo-600 dark:text-indigo-400" />{" "}
                        {modalLogInfo.distance !== null ? `${modalLogInfo.distance} meters` : "Verified"}
                      </p>
                    </div>
                    {modalLogInfo.location && (
                      <div className="col-span-2 space-y-1">
                        <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">GPS Coordinates</p>
                        <p className="text-slate-600 dark:text-slate-400 font-mono text-[11px] leading-tight">
                          Lat: {modalLogInfo.location.latitude?.toFixed(6)}, Lng: {modalLogInfo.location.longitude?.toFixed(6)}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default DailyFacultyAttendance;
